import React, { useState } from 'react';
import type { DealStageValue } from '../../types';
import { patchLeadStatus } from '../../services/api';
import LeadStatusBadge, { STATUS_LABELS } from './LeadStatusBadge';

const ALL_STAGES: DealStageValue[] = [
  'new',
  'contacted',
  'qualified',
  'quoted',
  'negotiation',
  'nurture',
  'won',
  'lost',
];

const LOST_REASONS: { value: string; label: string }[] = [
  { value: 'not_interested', label: 'Not interested' },
  { value: 'irrelevant', label: 'Irrelevant (wrong number, duplicate, etc.)' },
];

interface Props {
  leadId: number;
  currentStage: DealStageValue;
  currentLostReason?: string | null;
  onClose: () => void;
  onUpdated: () => void;
}

const LeadStatusEditor: React.FC<Props> = ({
  leadId, currentStage, currentLostReason, onClose, onUpdated,
}) => {
  const [stage, setStage] = useState<DealStageValue>(currentStage);
  const [lostReason, setLostReason] = useState<string>(currentLostReason ?? '');
  const [remark, setRemark] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState<string | null>(null);

  const lostReasonRequired = stage === 'lost';
  const canSave =
    !saving
    && !(lostReasonRequired && lostReason === '')
    && stage !== currentStage;

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await patchLeadStatus(leadId, {
        deal_stage:  stage,
        lost_reason: lostReasonRequired ? lostReason : null,
        remark:      remark.trim() || undefined,
      });
      onUpdated();
      onClose();
    } catch (e: any) {
      const msg = e?.response?.data?.error
        ?? e?.response?.data?.message
        ?? 'Failed to update status. Please try again.';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: 12, padding: 24, width: 480,
          maxWidth: 'calc(100vw - 32px)', boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
        }}
      >
        <h3 style={{ margin: '0 0 12px', fontSize: 18, fontWeight: 700 }}>
          Change deal stage
        </h3>
        <div style={{ fontSize: 13, color: '#64748b', marginBottom: 20 }}>
          Currently: <LeadStatusBadge status={currentStage} lostReason={currentLostReason} compact />
        </div>

        <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#334155', marginBottom: 6 }}>
          New stage
        </label>
        <select
          value={stage}
          onChange={e => setStage(e.target.value as DealStageValue)}
          style={{
            width: '100%', padding: '10px 12px', fontSize: 14,
            border: '1px solid #cbd5e1', borderRadius: 8, marginBottom: 16,
            background: '#fff',
          }}
        >
          {ALL_STAGES.map(s => (
            <option key={s} value={s}>{STATUS_LABELS[s]}</option>
          ))}
        </select>

        {lostReasonRequired && (
          <>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#334155', marginBottom: 6 }}>
              Reason <span style={{ color: '#dc2626' }}>*</span>
            </label>
            <select
              value={lostReason}
              onChange={e => setLostReason(e.target.value)}
              style={{
                width: '100%', padding: '10px 12px', fontSize: 14,
                border: '1px solid #cbd5e1', borderRadius: 8, marginBottom: 16,
                background: '#fff',
              }}
            >
              <option value="">Select a reason…</option>
              {LOST_REASONS.map(r => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
          </>
        )}

        <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#334155', marginBottom: 6 }}>
          Remark
        </label>
        <textarea
          value={remark}
          onChange={e => setRemark(e.target.value)}
          placeholder="Optional context on why this stage"
          rows={4}
          style={{
            width: '100%', padding: '10px 12px', fontSize: 14,
            border: '1px solid #cbd5e1', borderRadius: 8, marginBottom: 4,
            fontFamily: 'inherit', resize: 'vertical',
          }}
        />
        <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 16 }}>
          {remark.length}/2000
        </div>

        {error && (
          <div style={{
            background: '#fee2e2', color: '#991b1b', padding: '8px 12px',
            borderRadius: 6, fontSize: 13, marginBottom: 16,
          }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button
            onClick={onClose}
            disabled={saving}
            style={{
              padding: '8px 16px', fontSize: 14, fontWeight: 600,
              border: '1px solid #cbd5e1', background: '#fff', color: '#334155',
              borderRadius: 8, cursor: saving ? 'default' : 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!canSave}
            style={{
              padding: '8px 16px', fontSize: 14, fontWeight: 600,
              border: 'none', borderRadius: 8,
              background: canSave ? '#7c3aed' : '#cbd5e1',
              color: '#fff',
              cursor: canSave ? 'pointer' : 'default',
            }}
          >
            {saving ? 'Saving…' : 'Save stage'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LeadStatusEditor;
